import { placeActions } from '@/actions';
import { exportIdSlug } from '@/helpers';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { Place } from '@/types/state/shared';
import { useEffect, useReducer } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { z } from 'zod';

import { normalizePlacePageData } from '@/helpers/normalize';
import { fetchAndSetPlaceData } from './index.helpers';
import placeReducer from './index.reducer';
import { serviceActions } from '@/actions/serviceActions';
import { employeeActions } from '@/actions/employeeActions';

const usePlaceData = () => {
  const reduxDispatch = useAppDispatch();
  const ssrPlace = useAppSelector((state) => state.ssrPlace);
  const ssrIs404 = useAppSelector((state) => state.loading.is404);
  const { slugId } = useParams() as { slugId: string };
  const history = useHistory();
  const queryParams = new URLSearchParams(history.location.search);
  const isMpReviews = !['1', 'true'].includes(queryParams.get('service-reviews'));
  const isSistaminuten = ['1', 'true'].includes(queryParams.get('sistaminuten'));

  const { id, slug } = exportIdSlug(slugId);
  const [state, dispatch] = useReducer(placeReducer, {
    place: (() => {
      if (isSistaminuten && !ssrPlace?.lastMinuteDiscount) {
        return null;
      }

      if (!isSistaminuten && ssrPlace?.lastMinuteDiscount) {
        return null;
      }

      if (parseInt(ssrPlace?.id) !== parseInt(id)) {
        return null;
      }

      return ssrPlace;
    })(),
    loading: !ssrPlace,
    is404: Boolean(ssrIs404),
  });

  function setPlace(place: Place) {
    dispatch({ type: 'SET_PLACE', payload: place });

    const { normalizedPlace, normalizedServiceCategories, normalizedServices, normalizedEmployees } =
      normalizePlacePageData(place);

    reduxDispatch(
      placeActions.setPlaceData({
        ...normalizedPlace,
      }),
    );
    reduxDispatch(
      serviceActions.setServices({ ids: normalizedServices.ids as Array<number>, all: normalizedServices.all }),
    );
    reduxDispatch(
      serviceActions.setServiceCategories({
        ids: normalizedServiceCategories.ids as Array<number>,
        all: normalizedServiceCategories.all,
      }),
    );
    reduxDispatch(
      employeeActions.setEmployees({ ids: normalizedEmployees.ids as Array<number>, all: normalizedEmployees.all }),
    );

    if (place?.isExternalPlace && !place?.about?.showonmp) {
      dispatch({ type: 'SET_IS_404' });
    }
  }

  function setLoading(loading: boolean) {
    dispatch({ type: 'SET_LOADING', payload: !!loading });
  }

  function handleOnFetchError(error: unknown) {
    const is404 = z.object({ response: z.object({ status: z.literal(404) }) }).safeParse(error);
    if (is404.success) {
      dispatch({ type: 'SET_IS_404' });
    } else {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (state.place && state.place.id === parseInt(id)) {
      return;
    }
    window?.scrollTo(0, 0);
    fetchAndSetPlaceData({ id, slug, isMpReviews, isSistaminuten }, setPlace, handleOnFetchError, history);
    // eslint-disable-next-line
  }, [slugId]);

  return {
    place: state.place,
    loading: state.loading,
    is404: state.is404,
    isSistaminuten,
  };
};

export default usePlaceData;
